import clsx from 'clsx';
import { FieldHookConfig, useField } from 'formik';

import { FieldWrapper, FieldWrapperPassThroughProps } from './FieldWrapper';

export type InputFieldProps = FieldWrapperPassThroughProps & {
	type?: 'text' | 'email' | 'password';
	className?: string;
	inputClassName?: string;
	inputMode?: 'text' | 'numeric' | 'email';
	placeholder?: string;
	disabled?: boolean;
	icon?: string;
	maxLength?: number;
	autoComplete?: string;
	labelSrOnly?: boolean;
	errorMsg?: string;
	handleChange?: (e: React.ChangeEvent<HTMLInputElement> | undefined) => void;
	handleBlur?: (e: React.FocusEvent<HTMLInputElement> | undefined) => void;
};

export const InputField = (props: InputFieldProps & FieldHookConfig<string>) => {
	const {
		type,
		label,
		name = 'form-field',
		placeholder,
		width,
		className,
		inputClassName,
		inputMode = 'text',
		disabled = false,
		icon,
		maxLength,
		autoComplete,
		labelSrOnly = false,
		errorMsg,
		handleChange,
		handleBlur,
	} = props;
	const [field, meta, helpers] = useField(props);
	const { error, touched } = meta;
	const hasError = error || errorMsg;
	const showError = hasError && touched;

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		handleChange?.(e);
		field.onChange(e);
		if (inputMode === 'numeric') {
			// allow only numeric input
			const newVal = e.target.value.replace(/[^0-9.-]+/g, '');
			helpers.setValue(newVal);
		}
	};

	return (
		<FieldWrapper
			error={hasError}
			touched={touched}
			label={label}
			width={width}
			name={name}
			disabled={disabled}
			className={className}
			labelSrOnly={labelSrOnly}
		>
			{icon && (
				<img
					className='w-[24px] absolute left-4 top-[10px]'
					alt='input icon'
					src={icon}
					aria-hidden='true'
				/>
			)}
			<input
				type={type}
				id={name}
				placeholder={placeholder}
				autoComplete={autoComplete}
				{...field}
				className={clsx(
					'flex-1 min-w-0 block w-full h-11 p-3 text-base rounded-md border border-primaryBorder placeholder:text-base placeholder:text-disabledText',
					icon ? 'pl-12' : '',
					showError
						? 'text-error border-error placeholder:text-error focus:ring-error focus:border-error'
						: 'focus:ring-primaryBrand focus:border-primaryBrand',
					disabled
						? 'bg-disabledBackground border-primaryBorder cursor-not-allowed text-primaryTextDark'
						: '',
					inputClassName
				)}
				disabled={disabled}
				aria-invalid={showError ? 'true' : 'false'}
				aria-errormessage={showError ? `${name}-error` : ''}
				maxLength={maxLength}
				inputMode={inputMode}
				onChange={onChange}
				onBlur={(e) => {
					handleBlur?.(e);
					field.onBlur(e);
				}}
			/>
		</FieldWrapper>
	);
};
